import React from "react";
import { Box, ButtonBase } from "@mui/material";

/**
 * Segmented tab control shared by the language switcher and the theme toggle.
 *
 * variant="separate" (Wrapper-Buttons / Wrapper-Tab) — two bordered tabs with
 *   an 8px gap and no surrounding track. Active tab is filled; inactive is white.
 *   Used by the language switcher.
 *
 * variant="joined" (TabSwitch-View / Tab) — both tabs share one bordered,
 *   rounded container. Selected tab is a tinted, raised segment; unselected is
 *   white. Used by the light/dark theme toggle.
 *
 * options: [{ value, label, "data-testid" }]
 */
export default function SegmentedToggle({
  options,
  value,
  onChange,
  ariaLabel,
  variant = "separate",
  size = "md",
  "data-testid": dataTestId
}) {
  const joined = variant === "joined";
  const sm = size === "sm";

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      sx={{
        display: "inline-flex",
        flexDirection: "row",
        justifyContent: joined ? "flex-start" : "flex-end",
        alignItems: "center",
        gap: joined ? 0 : "8px",
        p: 0,
        borderRadius: "6px",
        ...(joined
          ? {
              // No overflow:hidden — it would clip the active tab's box-shadow.
              // Each button rounds its own outer corners via joinedRadius, so
              // the joined group stays visually clean without clipping.
              // Strict group height; boxSizing includes the 1px border.
              height: sm ? "20px" : "24px",
              minHeight: sm ? "20px" : "24px",
              maxHeight: sm ? "20px" : "24px",
              boxSizing: "border-box",
              border: "1px solid var(--new-border)"
            }
          : {})
      }}
    >
      {options.map((opt, i) => {
        const isActive = opt.value === value;
        // In the joined variant the tabs butt together: only the outer corners
        // are rounded, the touching inner edges stay square.
        const isFirst = i === 0;
        const isLast = i === options.length - 1;
        const r = "5px";
        const joinedRadius = `${isFirst ? r : "0"} ${isLast ? r : "0"} ${
          isLast ? r : "0"
        } ${isFirst ? r : "0"}`;
        return (
          <ButtonBase
            key={opt.value}
            disableRipple
            aria-pressed={isActive}
            aria-label={opt.label}
            data-testid={opt["data-testid"]}
            onClick={() => onChange(opt.value)}
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "2px",
              fontWeight: 600,
              lineHeight: 1,
              whiteSpace: "nowrap",
              transition:
                "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
              ...(joined
                ? {
                    // Group is fixed-height (set on the container); buttons fill
                    // it. boxSizing keeps the padding inside the strict height.
                    height: "100%",
                    boxSizing: "border-box",
                    p: "4px 12px",
                    fontSize: 12,
                    borderRadius: joinedRadius,
                    color: isActive
                      ? "var(--tabs-functional-active-content-neutral)"
                      : "var(--tabs-functional-inactive-content-neutral)",
                    backgroundColor: isActive
                      ? "var(--tabs-functional-active-background-neutral)"
                      : "var(--tabs-functional-inactive-background-neutral)",
                    boxShadow: isActive
                      ? "0 1px 3px 0 rgba(13, 0, 0, 0.1), 0 1px 2px -1px rgba(13, 0, 0, 0.1)"
                      : "none",
                    // Clip the shadow so it never spills onto the edge(s) where
                    // this button joins its neighbour. Negative insets let the
                    // shadow bleed out on the OUTER sides; the join side is
                    // clipped flush at 0. ~6px covers the 3px blur + offset.
                    ...(isActive
                      ? {
                          clipPath: `inset(-6px ${isLast ? "-6px" : "0px"} -6px ${
                            isFirst ? "-6px" : "0px"
                          })`
                        }
                      : {})
                  }
                : {
                    height: "24px",
                    minHeight: "24px",
                    maxHeight: "24px",
                    boxSizing: "border-box",
                    p: "4px 12px",
                    fontSize: 12,
                    borderRadius: "6px",
                    border: "1px solid var(--new-border)",
                    color: isActive
                      ? "var(--tabs-functional-active-content-neutral)"
                      : "var(--tabs-functional-inactive-content-neutral)",
                    backgroundColor: isActive
                      ? "var(--tabs-functional-active-background-neutral)"
                      : "var(--tabs-functional-inactive-background-neutral)",
                    borderColor: isActive
                      ? "var(--tabs-functional-active-background-neutral)"
                      : "var(--new-border)",
                    "&:hover": {
                      borderColor: isActive
                        ? "var(--tabs-functional-active-background-neutral)"
                        : "var(--new-border-accent)"
                    }
                  })
            }}
          >
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
